import { type SlideDomainEnum, SlideTemplateEnum } from "@/generated/graphql";
import { useAddSlide } from "@/lib/hooks/slides/useAddSlide";
import type { SubQuiz } from "@ai-teach/types";
import { subQuizToElements } from "@ai-teach/utils";

/**
 * Turns an inline sub-quiz back into a standalone freeform slide (the reverse of merging). Rebuilds
 * the cloze `elements` from the quiz and creates the slide immediately via `addSlide`. `tagIds` and
 * `domains` are inherited from the parent slide the quiz was ejected from — it lands in the same
 * section and credits the same dimensions as the content it came from.
 *
 * The new slide is persisted right away; removing the quiz from its parent is the caller's job (a Redux
 * edit that lands on the editor's next Save), so the parent must still be saved to avoid a duplicate.
 */
export function useEjectSubQuiz() {
  const { addSlide, loading } = useAddSlide();

  const ejectSubQuiz = async (
    quiz: SubQuiz,
    lessonId: string,
    tagIds: readonly string[],
    domains: SlideDomainEnum[]
  ): Promise<boolean> => {
    const name = quiz.question.trim() ? quiz.question.trim().slice(0, 80) : "Quiz";

    const created = await addSlide({
      lessonId,
      template: SlideTemplateEnum.FreeformCanvas,
      tagIds: [...tagIds],
      domains,
      name,
      elements: subQuizToElements(quiz),
    });

    return created.length > 0;
  };

  return { ejectSubQuiz, loading };
}
